import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Ban,
  ChevronRight,
  Copy,
  ExternalLink,
  FileCheck2,
  Send,
  SkipForward,
  Trash2,
} from 'lucide-react';
import {
  Button,
  Input,
  Modal,
  SegmentedControl,
  Skeleton,
  Textarea,
  toast,
} from '../../components/ui';
import { cn } from '../../lib/cn';
import { formatDate, formatMoney } from '../../lib/format';
import { CHANNEL_LABELS, agoLabel, chaseLabel, daysAgo, followUpLabel, sentLabel } from './chase';
import {
  useDeliverInvoice,
  useDeliveryStatus,
  useInvoiceDraft,
  useSetRecipients,
  type ContactChannel,
  type DeliveryStatus,
  type InvoiceDetail,
  type InvoiceReminder,
} from './api';
import { RecipientsField, useCopyMe, type RecipientsValue } from './recipients';

export const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

// ---------------------------------------------------------------------------
// The composer — one dialog, and it SENDS
//
// It used to build a `mailto:` href and push the operator out to their OS:
// "Nothing is sent from here — your own mail client sends it", with a Download
// PDF button underneath and the instruction "attach the invoice PDF to that
// draft — a mailto: cannot carry a file". On the highest-frequency action in
// the whole domain that was a step backwards from the spreadsheet it replaces.
//
// Now one button transmits: the server renders the PDF, attaches it, includes a
// link to the hosted invoice, and records the delivery — and, on a draft,
// issues the invoice in the same breath. What survived from the old dialog is
// the part that was right: every exit still writes a delivery record, and the
// message is still editable before it goes.
// ---------------------------------------------------------------------------

/** `send` issues the draft and starts the clock; `remind` chases the debt. */
/** `send` issues a draft · `remind` chases · `resend` sends the corrected invoice again. */
export type ComposerMode = 'send' | 'remind' | 'resend';

/** Position in a multi-invoice walk, when the composer is driving a queue. */
export interface ComposerQueue {
  position: number;
  total: number;
  onSkip: () => void;
}

export function isoToday(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
}

const DAY = 86_400_000;

/** "Net 30" / "Due on receipt", derived from the invoice's own dates. */
export function invoiceTerms(issueDate: string, dueDate: string): string {
  const days = Math.round(
    (new Date(`${dueDate}T12:00:00Z`).getTime() - new Date(`${issueDate}T12:00:00Z`).getTime()) /
      DAY,
  );
  return days <= 0 ? 'Due on receipt' : `Net ${days}`;
}

/**
 * Is this draft carrying a stale issue date the send would re-date?
 *
 * The dates themselves come from the server with the drafted message (it is
 * the one that re-dates on send), so this only decides whether to OFFER the
 * choice — a second local implementation of the date arithmetic is exactly how
 * a covering note ends up quoting terms the invoice does not carry.
 */
function canRedate(invoice: InvoiceDetail): boolean {
  return invoice.state === 'DRAFT' && invoice.issueDate < isoToday();
}

/** The whole message, as it goes on a clipboard. */
export function composerText(subject: string, body: string): string {
  return `Subject: ${subject}\n\n${body}`;
}

/** One environment fact: the short truth, and the fix for whoever runs the box. */
export interface EnvironmentFact {
  /** What it MEANS for the message about to go out. */
  headline: string;
  /** How to change it — for the developer, not the sender. */
  detail: string | null;
}

/** What the send button promises, in one sentence, from the server's own state. */
export function deliveryNote(status: DeliveryStatus | undefined): EnvironmentFact | null {
  if (!status) return null;
  if (status.mode === 'live') return null;
  if (status.mode === 'file') {
    return {
      headline: `Nothing is transmitted — message and PDF go to ${status.fileDir}/.`,
      detail: 'EMAIL_MODE=live (with an EMAIL_ALLOWLIST) sends for real.',
    };
  }
  return { headline: status.reason ?? 'Outbound email is disabled (EMAIL_MODE=off).', detail: null };
}

/**
 * ⚠ WHAT THE PAY BUTTON ON THE INVOICE YOU ARE ABOUT TO SEND WILL DO.
 *
 * `invoice-payment.ts` documents its `reason` string as "printed beside the
 * button / in Settings" and it was printed in NEITHER, so the only way to
 * discover that every invoice going out carried a TEST MODE pay button was to
 * open the client's own hosted page and look at it. It is stated here, above
 * the fold, before anything is sent — the same rule EMAIL_MODE already obeys.
 *
 * ⚠ `scope` exists because this sentence headed a NINE-invoice run reading
 * "the 'Pay now' button on **this invoice**". A dialog that miscounts what it
 * is about teaches the reader to stop reading it.
 */
export function paymentNote(
  status: DeliveryStatus | undefined,
  scope: 'one' | 'many' = 'one',
): EnvironmentFact | null {
  const payments = status?.payments;
  if (!payments) return null;
  if (payments.mode === 'live') return null;
  const subject = scope === 'many' ? 'these invoices' : 'this invoice';
  if (payments.mode === 'test') {
    return {
      headline: `Card payments are in TEST MODE — the pay button on ${subject} charges nothing.`,
      detail:
        'STRIPE_SECRET_KEY + STRIPE_WEBHOOK_SECRET take real cards; INVOICE_CARD_PAYMENTS=off removes the button.',
    };
  }
  return {
    headline: `${payments.reason ?? 'Card payment is switched off.'} ${
      scope === 'many' ? 'The messages' : 'The message'
    } will offer bank transfer only.`,
    detail: null,
  };
}

/**
 * ⚠ **THREE STACKED WARNINGS ARE NOT THREE TIMES THE HONESTY.**
 *
 * The composer opened with the file-mode banner, the Stripe TEST MODE banner
 * and the "Sending issues invoice #1154" explainer, each a full-width padded
 * box: at 1440×900 they filled the top 45% of the modal and pushed Subject and
 * the message body below a "scroll for more" fade; at 390px the first screen
 * was nothing but banners — no To field, no subject, no body. Two of the three
 * are development facts about the BOX, not about the invoice, and the modal was
 * giving them the same weight as the task.
 *
 * So the environment facts are ONE strip, one line each, with the env-var
 * remedy in a second, quieter clause. Not one word is dropped — the sender
 * still learns before pressing Send that nothing transmits and that the pay
 * button is a test — but the answer costs a third of the height, and the
 * controls are back above the fold.
 */
export function EnvironmentStrip({
  facts,
  className,
}: {
  facts: Array<EnvironmentFact | null>;
  className?: string;
}) {
  const [showDetail, setShowDetail] = useState(false);
  const real = facts.filter((f): f is EnvironmentFact => Boolean(f));
  if (real.length === 0) return null;
  const remedies = real.filter((f) => f.detail);
  return (
    <div
      className={cn(
        'rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-secondary',
        className,
      )}
    >
      <ul className="space-y-1">
        {real.map((fact) => (
          <li key={fact.headline} className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
            <span className="min-w-0 text-caption font-semibold leading-snug">{fact.headline}</span>
          </li>
        ))}
      </ul>
      {/* ⚠ THE HEADLINE IS THE PROMISE; THE REMEDY IS FOR WHOEVER RUNS THE BOX.
          Both used to be one paragraph, and on a 390px phone the two of them
          filled the first screen — no To field, no subject, no body. What a
          sender must know before pressing Send is above; how to change it is
          one tap away, and never on the critical path. */}
      {remedies.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setShowDetail((v) => !v)}
            aria-expanded={showDetail}
            className="mt-1 inline-flex items-center gap-1 rounded text-caption font-semibold text-grey-600 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
          >
            How to change this
            <ChevronRight
              className={cn('h-3 w-3 transition-transform', showDetail && 'rotate-90')}
              aria-hidden
            />
          </button>
          {showDetail && (
            <ul className="mt-1 space-y-0.5 text-caption leading-snug text-grey-600">
              {remedies.map((fact) => (
                <li key={`why:${fact.headline}`}>{fact.detail}</li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

/** One logged chase in the modal's history strip, with an undo. */
function ReminderHistoryRow({
  reminder,
  onRemove,
  removing,
}: {
  reminder: InvoiceReminder;
  onRemove: () => void;
  removing: boolean;
}) {
  return (
    <li className="flex items-baseline justify-between gap-3 py-1">
      <span className="min-w-0">
        <span className="num font-medium text-secondary">{formatDate(reminder.sentAt)}</span>{' '}
        <span className="text-grey-500">
          · {CHANNEL_LABELS[reminder.channel] ?? reminder.channel}
          {reminder.to ? ` · ${reminder.to}` : ''}
        </span>
        {reminder.note && (
          <span className="block truncate text-caption text-grey-500">{reminder.note}</span>
        )}
      </span>
      <span className="flex shrink-0 items-center gap-2">
        <span className="num text-caption text-grey-400">{agoLabel(daysAgo(reminder.sentAt))}</span>
        <button
          type="button"
          aria-label={`Remove the reminder logged on ${formatDate(reminder.sentAt)}`}
          title="Not actually sent? Remove it from the log"
          disabled={removing}
          onClick={onRemove}
          className="rounded p-1 text-grey-400 transition-colors hover:bg-primary-50 hover:text-primary-600 disabled:opacity-50"
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
        </button>
      </span>
    </li>
  );
}

/**
 * The one composer. `mode="send"` issues a draft; `mode="remind"` chases an
 * open one — identical machinery, because both are a delivery to a named
 * address and both have to be recorded.
 *
 * The footer holds exactly three exits and every one of them writes a record:
 * **Send now** transmits (and issues, on a draft), **Copy text** hands you the
 * message for an inbox we cannot reach, and **Log it without sending** covers
 * the post and the phone call.
 */
export function ComposerModal({
  mode,
  invoice,
  open,
  onOpenChange,
  onCommit,
  onSent,
  onRemoveReminder,
  busy,
  queue,
}: {
  mode: ComposerMode;
  invoice: InvoiceDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Records a delivery this app did not perform (copied / logged manually). */
  onCommit: (channel: ContactChannel, payload: { to: string; subject: string; note: string; issueDate?: string }) => void;
  /** Fired after the server has actually transmitted (or filed) the message. */
  onSent?: () => void;
  onRemoveReminder?: (reminderId: string) => void;
  busy: boolean;
  /** Present when this composer is walking a multi-invoice queue. */
  queue?: ComposerQueue;
}) {
  const sending = mode === 'send';
  // The corrected invoice, sent again AS AN INVOICE — not a chase.
  const resending = mode === 'resend';
  // Send only: keep the draft's own issue date instead of re-dating to today.
  const [keepIssueDate, setKeepIssueDate] = useState(false);
  const redatable = canRedate(invoice);
  const issueDate = keepIssueDate ? invoice.issueDate : undefined;
  const draftQuery = useInvoiceDraft(invoice.id, {
    enabled: open,
    issueDate: issueDate ?? null,
    mode: resending ? 'resend' : null,
  });
  const draft = draftQuery.data;
  const status = useDeliveryStatus();
  const deliver = useDeliverInvoice();

  /**
   * The date the SERVER would issue under, learned from the draft it returns.
   *
   * ⚠ The browser's midnight and the API's are not the same instant: `@db.Date`
   * fields are UTC, so between 19:00 Chicago and midnight UTC a locally
   * computed "today" is a day behind the date the invoice will actually carry.
   * The toggle used to be labelled from `isoToday()` and read "Today · Aug 12"
   * above a line saying the invoice would be issued Aug 13.
   */
  const [serverToday, setServerToday] = useState<string | null>(null);
  useEffect(() => {
    if (!keepIssueDate && draft) setServerToday(draft.issueDate);
  }, [keepIssueDate, draft]);

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  /**
   * THE AUDIENCE, not "the address". An invoice used to be sendable to exactly
   * one person, because a client could hold exactly one; the AP inbox, the
   * client's PM and a copy for our own records meant sending from here and then
   * forwarding by hand. `copyMe` is remembered across messages — see
   * `useCopyMe`.
   */
  const [copyMe, setCopyMe] = useCopyMe();
  const [recipients, setRecipients] = useState<RecipientsValue>({
    to: invoice.client.billingEmail ? [invoice.client.billingEmail] : [],
    cc: invoice.client.billingEmailCc ?? [],
    copyMe,
  });
  const setAudience = (next: RecipientsValue) => {
    setRecipients(next);
    if (next.copyMe !== copyMe) setCopyMe(next.copyMe);
  };
  const audience = [...recipients.to, ...recipients.cc];
  const saveRecipients = useSetRecipients();
  const [note, setNote] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrolledToEnd, setScrolledToEnd] = useState(true);

  // Re-prime for a new invoice (the queue swaps them under a modal that stays
  // open) and whenever the dialog is re-opened.
  const primedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      primedFor.current = null;
      return;
    }
    if (primedFor.current === invoice.id) return;
    primedFor.current = invoice.id;
    setKeepIssueDate(false);
    setRecipients({
      to: invoice.client.billingEmail ? [invoice.client.billingEmail] : [],
      cc: invoice.client.billingEmailCc ?? [],
      copyMe,
    });
    setNote('');
    scrollRef.current?.scrollTo({ top: 0 });
    // `copyMe` is a remembered habit, not a per-invoice value: re-priming on a
    // change to it would reset the chips somebody had just edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoice.id, invoice.client.billingEmail, invoice.client.billingEmailCc]);

  // The drafted text is the SERVER's, and it follows the issue date the user
  // picks — "due Sep 11" in the body has to agree with the invoice being
  // issued, and only one side of the wire knows how a stale draft re-dates.
  const draftKey = draft ? `${draft.id}:${draft.issueDate}` : null;
  const primedDraft = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !draft || !draftKey) {
      if (!open) primedDraft.current = null;
      return;
    }
    if (primedDraft.current === draftKey) return;
    primedDraft.current = draftKey;
    setSubject(draft.subject);
    setBody(draft.body);
  }, [open, draft, draftKey]);

  const syncScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setScrolledToEnd(el.scrollHeight - el.clientHeight - el.scrollTop < 8);
  }, []);
  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(syncScroll, 60);
    return () => window.clearTimeout(t);
  }, [open, syncScroll, invoice.id, draftKey]);

  const payload = () => ({
    // The record names the WHOLE audience — "we never received it" is answered
    // by addresses, not by a count.
    to: audience.join(', '),
    subject: subject.trim(),
    note: note.trim(),
    ...(sending ? { issueDate: draft?.issueDate } : {}),
  });

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(composerText(subject, body));
      // Copying IS the delivery when you paste it into an inbox we cannot
      // reach — so it counts, and it is logged.
      onCommit('copied', payload());
    } catch {
      toast.error('Could not copy — select the text and copy manually');
    }
  };

  const send = () => {
    deliver.mutate(
      {
        id: invoice.id,
        to: recipients.to,
        cc: recipients.cc,
        copyMe: recipients.copyMe,
        subject: subject.trim(),
        body,
        note: note.trim() || null,
        issueDate: sending ? (draft?.issueDate ?? null) : null,
        ...(resending ? { mode: 'resend' as const } : {}),
      },
      {
        onSuccess: (res) => {
          const where = res.delivery.disposition === 'sent' ? 'Sent to' : 'Filed for';
          const who =
            res.delivery.recipients.length > 1
              ? `${res.delivery.recipients.length} recipients`
              : (res.delivery.recipients[0] ?? res.delivery.to);
          toast.success(
            `${where} ${who}${
              res.delivery.attachment ? ` · ${res.delivery.attachment} attached` : ''
            }${sending ? ` · #${invoice.number} issued` : resending ? ` · updated #${invoice.number}` : ''}${
              res.delivery.learned.length > 0
                ? ` · ${res.delivery.learned.join(', ')} saved to ${invoice.client.name}`
                : ''
            }`,
          );
          onSent?.();
        },
        onError: (e) =>
          toast.error(e instanceof Error ? e.message : 'The message could not be sent'),
      },
    );
  };

  const history = invoice.reminders;
  const chaseLine = chaseLabel(invoice.chase, invoice.isLate);
  const dates = { issueDate: draft?.issueDate ?? invoice.issueDate, dueDate: draft?.dueDate ?? invoice.dueDate };
  const terms = invoiceTerms(dates.issueDate, dates.dueDate);
  const modeNote = deliveryNote(status.data);
  const payNote = paymentNote(status.data);
  const badAddress = audience.some((v) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v));
  /**
   * ⚠ THE INVOICE ITSELF REFUSES — paid, void, or not yet issued.
   *
   * The detail page only offers the composer on a sendable invoice, but
   * `?remind=1` / `?send=1` opens it on ANY invoice, so a stale tab or a
   * bookmarked row lands here on one that is already settled. The server now
   * refuses such a send before it transmits (`invoice-sendability.ts`); this is
   * the same fact, said before the button is pressed rather than after.
   *
   * `overridable` separates it from "this client has no billing email", which
   * typing an address genuinely fixes and which must stay sendable.
   */
  const stateBlock = draft && draft.blocked && !draft.overridable ? draft.blocked : null;
  const canSend =
    !stateBlock &&
    audience.length > 0 &&
    !badAddress &&
    status.data?.mode !== 'off' &&
    Boolean(draft);
  const working = busy || deliver.isPending;
  const others = invoice.clientBalance;

  const title = queue
    ? `${sending ? 'Send' : 'Chase'} #${invoice.number} · ${queue.position} of ${queue.total}`
    : sending
      ? `Send invoice #${invoice.number}`
      : resending
        ? `Send the updated invoice #${invoice.number}`
        : 'Send a payment reminder';

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      // The dialog never scrolls; its BODY does (below), so the button that
      // actually sends cannot fall off the bottom of a laptop screen.
      className="max-h-[92vh]"
      title={title}
      description={
        sending
          ? `${invoice.client.name} · ${formatMoney(invoice.amount)} · ${terms}, due ${formatDate(
              dates.dueDate,
            )}`
          : invoice.isLate && !resending
            ? `${invoice.client.name} · ${formatMoney(invoice.dueAmount)} due · ${plural(
                invoice.daysOverdue,
                'day',
                'days',
              )} overdue`
            : `${invoice.client.name} · ${formatMoney(invoice.dueAmount)} due ${formatDate(
                invoice.dueDate,
              )}`
      }
      footer={
        <div className="flex w-full flex-col gap-2 border-t border-grey-100 pt-4 sm:flex-row sm:items-center sm:justify-end">
          <div className="flex items-center gap-2 sm:contents">
            <Button
              variant="ghost"
              className="min-w-0 flex-1 sm:mr-auto sm:flex-none"
              onClick={() => onCommit('manual', payload())}
              loading={working}
              /* A state block refuses the record too — the server would 400 on
                 it, so offering the button is offering a dead end. */
              disabled={Boolean(stateBlock)}
              title={
                stateBlock
                  ? stateBlock
                  : sending
                    ? 'Posted it, or sent it from your own inbox — issue the invoice and record that'
                    : resending
                      ? 'Sent the updated invoice from your own inbox — record that it went'
                      : 'Chased by phone or from your own inbox — record it so the follow-up queue stays honest'
              }
            >
              Log it without sending
            </Button>
            {queue && (
              <Button variant="ghost" className="shrink-0" onClick={queue.onSkip} disabled={working}>
                <SkipForward className="h-4 w-4" aria-hidden />
                Skip
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2 sm:contents">
            <Button
              variant="outline"
              className="min-w-0 flex-1 sm:flex-none"
              onClick={copy}
              loading={working}
              /**
               * ⚠ THE CLIPBOARD IS A SEND PATH. The bulk dialog already learned
               * this for the not-due rail ("Copy all as text" used to hand over
               * the eight messages "Chase all" refused); a dunning letter for a
               * PAID invoice pasted from an operator's own inbox reaches the
               * client exactly as surely as one we transmit, and with less
               * supervision.
               */
              disabled={Boolean(stateBlock)}
              title={stateBlock ?? undefined}
            >
              <Copy className="h-4 w-4" aria-hidden />
              Copy text
            </Button>
            <Button
              className="min-w-0 flex-1 sm:flex-none"
              onClick={send}
              disabled={!canSend}
              loading={working}
              title={
                status.data?.mode === 'off'
                  ? (status.data.reason ?? 'Outbound email is disabled')
                  : `Emails ${
                      audience.length > 1
                        ? `${audience.length} recipients (${audience.join(', ')})`
                        : (audience[0] ?? 'the client')
                    } with the invoice PDF attached`
              }
            >
              <Send className="h-4 w-4" aria-hidden />
              {sending ? 'Send invoice' : resending ? 'Send updated invoice' : 'Send reminder'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="relative">
        <div
          ref={scrollRef}
          onScroll={syncScroll}
          className="-mx-1 max-h-[52vh] overflow-y-auto px-1 pb-1 sm:max-h-[66vh]"
        >
          {/* What "Send" will actually do, at the top where it cannot be
              scrolled past. This app never mails a real client from a
              development box (three independent gates in `PortalConfig`), and
              a button that says "Send" has to admit that before it is pressed,
              not after. */}
          {/* …and what the PAY button on the invoice will do, from the same
              server state. Both are facts about the BOX rather than about this
              invoice, so they share one quiet strip — see `EnvironmentStrip`. */}
          <EnvironmentStrip facts={[modeNote, payNote]} className="mb-3" />
          {stateBlock && (
            <p className="mb-3 flex items-start gap-2 rounded-lg border border-status-late/40 bg-status-late/5 px-3 py-1.5 text-caption leading-snug text-secondary">
              <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-late" aria-hidden />
              <span>
                <span className="font-semibold">{stateBlock}</span> Nothing will be transmitted —
                addressing it differently does not change what the invoice is.
              </span>
            </p>
          )}
          {resending ? (
            <p className="mb-3 flex items-start gap-2 rounded-lg border border-accent/30 bg-accent/5 px-3 py-1.5 text-caption leading-snug text-secondary">
              <Send className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent-700" aria-hidden />
              <span>
                <span className="font-semibold">Sends the updated invoice #{invoice.number}</span> — PDF
                attached, still due <span className="num">{formatDate(invoice.dueDate)}</span>. It is
                not a payment reminder and does not count as a chase.
              </span>
            </p>
          ) : sending ? (
            /* What the buttons below actually do to the invoice. A state change
               this consequential must not be a surprise discovered afterwards. */
            <p className="mb-3 flex items-start gap-2 rounded-lg border border-accent/30 bg-accent/5 px-3 py-1.5 text-caption leading-snug text-secondary">
              <Send className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent-700" aria-hidden />
              <span>
                <span className="font-semibold">Sending issues invoice #{invoice.number}</span> —
                draft to open, PDF attached, payment due{' '}
                <span className="num">{formatDate(dates.dueDate)}</span>.
              </span>
            </p>
          ) : (
            /* What has already been tried — so the second chase can say something
               the first one didn't, and nobody chases the same invoice twice in a
               morning. */
            <div className="mb-4 rounded-lg border border-grey-200 bg-grey-50 px-3 py-2.5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p
                  className={cn(
                    'text-body-sm font-semibold',
                    invoice.chase.count === 0 && invoice.isLate
                      ? 'text-status-late'
                      : 'text-secondary',
                  )}
                >
                  {chaseLine ?? 'Not chased yet'}
                </p>
                {followUpLabel(invoice.chase, invoice.isLate) && (
                  <p
                    className={cn(
                      'num text-caption',
                      invoice.chase.needsChase ? 'font-semibold text-status-late' : 'text-grey-500',
                    )}
                  >
                    {followUpLabel(invoice.chase, invoice.isLate)}
                  </p>
                )}
              </div>
              {invoice.sentRecord && (
                <p className="mt-1 text-caption text-grey-500">
                  {sentLabel(invoice.sentRecord)} ·{' '}
                  {CHANNEL_LABELS[invoice.sentRecord.channel] ?? invoice.sentRecord.channel}
                </p>
              )}
              {history.length > 0 && (
                <ul className="mt-1.5 divide-y divide-grey-200/70 text-body-sm">
                  {history.slice(0, 2).map((r) => (
                    <ReminderHistoryRow
                      key={r.id}
                      reminder={r}
                      removing={working}
                      onRemove={() => onRemoveReminder?.(r.id)}
                    />
                  ))}
                  {history.length > 2 && (
                    <li className="pt-1 text-caption text-grey-500">
                      +{history.length - 2} earlier — the full history is in Activity.
                    </li>
                  )}
                </ul>
              )}
            </div>
          )}

          {/* One client, one conversation. Chasing this invoice while the same
              client's other one sits late is two emails where one would do. */}
          {!sending && !resending && others && others.otherCount > 0 && (
            <div className="mb-4 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2.5 text-body-sm text-secondary">
              <p>
                <span className="font-semibold">
                  {invoice.client.name} owes{' '}
                  <span className="num">
                    {formatMoney(invoice.dueAmount + others.otherDue, { whole: true })}
                  </span>{' '}
                  across {others.otherCount + 1} invoices
                </span>
                {others.otherLateCount > 0 && (
                  <span className="text-grey-600">
                    {' '}
                    · {others.otherLateCount} of the others {others.otherLateCount === 1 ? 'is' : 'are'} also late
                  </span>
                )}
                .
              </p>
              <p className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-caption text-grey-600">
                {others.others.slice(0, 4).map((o) => (
                  <Link
                    key={o.id}
                    to={`/invoices/${o.id}`}
                    className="num font-semibold text-accent-700 hover:underline"
                  >
                    #{o.number} {formatMoney(o.dueAmount)}
                    {o.isLate ? ` · ${o.daysOverdue}d late` : ` · due ${formatDate(o.dueDate)}`}
                  </Link>
                ))}
                {others.otherCount > 4 && <span>+{others.otherCount - 4} more</span>}
              </p>
            </div>
          )}

          {/* Harvest lets you set the issue date at send time; a draft written
              last week should not silently restart its terms today. */}
          {sending && redatable && (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-grey-200 px-3 py-2.5">
              <div className="min-w-0">
                <p className="text-body-sm font-semibold text-secondary">Issue date</p>
                <p className="num text-caption text-grey-500">
                  {formatDate(dates.issueDate)} · {terms} · due {formatDate(dates.dueDate)}
                </p>
              </div>
              <SegmentedControl
                ariaLabel="Issue date"
                value={keepIssueDate ? 'keep' : 'today'}
                onChange={(v) => setKeepIssueDate(v === 'keep')}
                options={[
                  {
                    value: 'today',
                    label: `Today · ${formatDate(serverToday ?? isoToday())}`,
                    title: 'Re-issue today — payment terms start now',
                  },
                  {
                    value: 'keep',
                    label: `Keep ${formatDate(invoice.issueDate)}`,
                    title: 'Issue it under the date the draft was written, terms unchanged',
                  },
                ]}
              />
            </div>
          )}

          {!invoice.client.billingEmail && (invoice.client.billingEmailCc ?? []).length === 0 && (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2.5 text-body-sm text-secondary">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
              <span>
                {invoice.client.name} has no billing email on file. Type one into the To row below
                and it is saved against the client, so the next {sending ? 'invoice' : 'reminder'}{' '}
                addresses itself — or set it on the{' '}
                <Link
                  to={`/clients/${invoice.client.id}`}
                  className="font-semibold text-accent-700 hover:underline"
                >
                  client record
                </Link>
                .
              </span>
            </div>
          )}

          {draftQuery.isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : draftQuery.isError ? (
            <p className="rounded-lg border border-status-late-border bg-status-late-bg px-3 py-2.5 text-body-sm text-status-late">
              {draftQuery.error instanceof Error
                ? draftQuery.error.message
                : 'Could not draft the message.'}
            </p>
          ) : (
            <div className="space-y-4">
              {/* ⚠ THE BIGGEST GAP THIS DOMAIN HAD. One text box, one address,
                  one client contact — so AP plus the client's PM plus a copy
                  for our own records meant sending from here and then
                  forwarding by hand from Gmail, every invoice, forever. */}
              <RecipientsField
                value={recipients}
                onChange={setAudience}
                copyToAddress={draft?.copyTo ?? null}
                known={[
                  ...(invoice.client.billingEmail ? [invoice.client.billingEmail] : []),
                  ...(invoice.client.billingEmailCc ?? []),
                ]}
                clientName={invoice.client.name}
                saving={saveRecipients.isPending}
                onSaveToClient={() =>
                  saveRecipients.mutate(
                    { id: invoice.id, to: recipients.to, cc: recipients.cc },
                    {
                      onSuccess: () =>
                        toast.success(
                          `${invoice.client.name} now bills to ${audience.join(', ') || 'nobody'}`,
                        ),
                      onError: (e) =>
                        toast.error(e instanceof Error ? e.message : 'Could not save the list'),
                    },
                  )
                }
              />
              <Input label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
              <Textarea
                label="Message"
                rows={7}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                hint={`Sent from ${status.data?.from ?? 'your billing address'} — replies come straight back to you.`}
              />
              {/* No attach step, no download-and-find-the-file: the PDF rides
                  with the message and the client gets a link to the same
                  document online. */}
              <div className="rounded-lg border border-grey-200 bg-grey-50 px-3 py-2.5">
                <p className="flex items-start gap-2 text-body-sm text-secondary">
                  <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-status-paid" aria-hidden />
                  <span>
                    <span className="font-semibold">Invoice-{invoice.number}.pdf is attached</span>{' '}
                    automatically.
                    {draft?.viewUrl ? (
                      <>
                        {' '}
                        The message also links to the invoice online, where the client can read it
                        and download the PDF any time.
                      </>
                    ) : null}
                  </span>
                </p>
                {draft?.viewUrl && (
                  <a
                    href={draft.viewUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1.5 inline-flex items-center gap-1.5 text-caption font-semibold text-accent-700 hover:underline"
                  >
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                    Open the client's view
                  </a>
                )}
              </div>
              <Input
                label="Internal note (optional)"
                placeholder={sending ? 'Agreed 45-day terms on this one' : 'Escalated to their AP manager'}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                hint="Kept on the record here. Never shown to the client."
              />
            </div>
          )}
        </div>
        {/* Something is still below the fold — say so, rather than letting the
            message end mid-sentence at an invisible line. */}
        {!scrolledToEnd && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 flex h-9 items-end justify-end bg-gradient-to-t from-white via-white/85 to-transparent pr-1"
          >
            <span className="flex items-center gap-1 rounded-full bg-white/90 px-2 text-caption font-semibold text-grey-400 shadow-sm">
              scroll for more
              <ChevronRight className="h-3 w-3 rotate-90" aria-hidden />
            </span>
          </div>
        )}
      </div>
    </Modal>
  );
}
